import { useEffect, useState } from "react";
import { Plus, Search, Trash2, ShieldAlert, AlertOctagon } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import SearchableSelect from "../../components/SearchableSelect";

const DANNI_PRESET = [
  "Mancato pagamento", "Restituzione auto danneggiata", "Sinistro non denunciato",
  "Multe non pagate", "Carburante mancante", "Furto/Appropriazione",
  "Documenti falsi", "Guida in stato alterato", "Targhe scadute",
];

export default function SharedBlacklist() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState({ codice_fiscale: "", nome: "", cognome: "", ragione_sociale: "", motivo: "", dettagli: [], importo_danno: 0, note: "" });

  const load = () => api.get("/shared-blacklist", { params: { search } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [search]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/shared-blacklist", { ...form, importo_danno: +form.importo_danno });
      toast.success(r.data.merged ? `Segnalazione aggiunta (${r.data.total_segnalazioni} totali)` : "Cliente inserito in Blacklist Condivisa");
      setShow(false);
      setForm({ codice_fiscale: "", nome: "", cognome: "", ragione_sociale: "", motivo: "", dettagli: [], importo_danno: 0, note: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const remove = async (cf) => {
    if (!window.confirm(`Rimuovere ${cf} dalla blacklist condivisa?`)) return;
    try { await api.delete(`/shared-blacklist/${cf}`); toast.success("Rimosso"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="shared-bl-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            Blacklist Condivisa <ShieldAlert className="text-[#ff0033]" />
          </h1>
          <p className="text-sm text-white/45 mt-1">{list.length} soggetti segnalati cross-tenant · ogni nuova segnalazione si somma a quelle esistenti</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid="sbl-search" placeholder="Cerca CF, nome, società, causale…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm w-72 focus:border-[#ff0033]/60 focus:outline-none" />
          </div>
          <button data-testid="sbl-add" onClick={() => setShow(true)} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
            <Plus size={14} /> Nuova Segnalazione
          </button>
        </div>
      </div>

      <div className="glass rounded-2xl p-4 text-xs text-white/55 flex items-start gap-3">
        <AlertOctagon size={16} className="text-amber-300 shrink-0 mt-0.5" />
        <div>
          La <b>Blacklist Condivisa</b> è una rubrica cross-tenant accessibile a tutte le società che usano FabriGroup Rent Manager.
          I clienti con CF segnalato qui <b>non possono firmare nuovi contratti</b> finché la causale non viene chiarita.
          Le segnalazioni della tua società vengono aggiunte automaticamente quando metti un cliente in blacklist locale.
        </div>
      </div>

      <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">Codice Fiscale</th>
              <th className="text-left p-4">Soggetto</th>
              <th className="p-4">Segnalazioni</th>
              <th className="p-4">Ultima segnalazione</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.map((e) => (
              <tr key={e.codice_fiscale} onClick={() => setDetails(e)} className="hover:bg-white/[0.02] cursor-pointer" data-testid={`sbl-${e.codice_fiscale}`}>
                <td className="p-4 font-mono text-xs font-bold text-[#ff5577]">{e.codice_fiscale}</td>
                <td className="p-4">
                  <div className="font-medium">{e.ragione_sociale || `${e.nome || ""} ${e.cognome || ""}`}</div>
                </td>
                <td className="p-4 text-center">
                  <span className="font-display font-bold text-lg neon-text">{e.segnalazioni?.length || 0}</span>
                </td>
                <td className="p-4 text-center font-mono text-[10px] text-white/40">{(e.updated_at || "").slice(0, 10)}</td>
                <td className="p-4 text-right" onClick={(ev) => ev.stopPropagation()}>
                  <button onClick={() => remove(e.codice_fiscale)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-white/40">Nessuna segnalazione condivisa.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* ADD */}
      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="sbl-form">
            <div className="font-display text-2xl font-bold mb-5 flex items-center gap-2"><ShieldAlert size={20} className="text-[#ff0033]" />Nuova Segnalazione</div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Codice Fiscale *</label>
                <input data-testid="sbl-cf" required value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                <input placeholder="Cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                  className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <input placeholder="Ragione Sociale (se società)" value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Causale * (ricerca tra preset esistenti o digita nuova)</label>
                <SearchableSelect
                  testid="sbl-causale"
                  value={form.motivo}
                  onChange={(v) => setForm({ ...form, motivo: v })}
                  allowFreeText
                  placeholder="Es. Ammaccatura, Graffio, Mancato pagamento…"
                  options={Array.from(new Set([
                    ...DANNI_PRESET,
                    ...list.flatMap((e) => (e.segnalazioni || []).map((s) => s.motivo).filter(Boolean)),
                  ])).sort()}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40 mb-2 block">Dettagli</label>
                <div className="flex flex-wrap gap-2">
                  {DANNI_PRESET.map((d) => {
                    const active = form.dettagli.includes(d);
                    return (
                      <button key={d} type="button" onClick={() => setForm({ ...form, dettagli: active ? form.dettagli.filter(x => x !== d) : [...form.dettagli, d] })}
                        className={`text-[11px] px-2.5 py-1 rounded-full border ${active ? "bg-[#ff0033]/20 text-[#ff5577] border-[#ff0033]/40" : "bg-white/[0.03] border-white/10 text-white/60"}`}>
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Importo danno €</label>
                <input type="number" step="0.01" value={form.importo_danno} onChange={(e) => setForm({ ...form, importo_danno: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <textarea placeholder="Note interne" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="sbl-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">Segnala</button>
            </div>
          </form>
        </div>
      )}

      {/* DETAILS */}
      {details && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setDetails(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl glass-strong rounded-3xl p-6 max-h-[90vh] overflow-auto">
            <div className="font-display text-2xl font-bold mb-3">Dettagli Segnalazioni</div>
            <div className="text-sm mb-4 p-3 rounded-xl bg-[#ff0033]/10 border border-[#ff0033]/30">
              <div className="font-mono font-bold text-[#ff5577]">{details.codice_fiscale}</div>
              <div className="text-white/70">{details.ragione_sociale || `${details.nome || ""} ${details.cognome || ""}`}</div>
            </div>
            <div className="space-y-3">
              {(details.segnalazioni || []).map((s, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-white/40 uppercase tracking-widest">Società: {s.societa}</div>
                    <div className="text-[10px] text-white/40 font-mono">{(s.ts || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="font-medium">{s.motivo}</div>
                  {s.dettagli?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {s.dettagli.map((d, j) => <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-[#ff0033]/15 text-[#ff5577] border border-[#ff0033]/30">{d}</span>)}
                    </div>
                  )}
                  {s.importo_danno > 0 && <div className="mt-2 text-sm">Danno: <b className="neon-text">€{s.importo_danno}</b></div>}
                  <div className="mt-2 text-[10px] text-white/40">Segnalato da: {s.segnalatore_nome}</div>
                  {s.note && <div className="mt-2 text-xs text-white/60 italic">"{s.note}"</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
